import styled, { keyframes } from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../../utils/constants";
import { toneColor } from "../../../../../utils";
import { HERO_CHART_HEIGHT } from "../../constants";
import { GrowthTone } from "./types";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const HeroCard = styled.section`
  display: flex;
  flex-direction: column;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-radius: ${SPACING.md}px;
  background: var(--hero-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

export const PortfolioLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

// Single compressed line: the figure and its gain share a baseline, so the
// chart below can own the bulk of the card. Wraps the chip under the value
// only when the row runs out of room.
export const Headline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: ${SPACING.md}px;
  row-gap: ${SPACING.xs}px;
  margin-top: ${SPACING.lg}px;
`;

export const CurrentValue = styled.p`
  margin: 0;
  font-size: var(--text-display);
  font-weight: var(--font-weight-bold);
  line-height: 1.05;
  letter-spacing: -0.015em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--color-text-default);

  @media ${QUERIES.tabletAndUp} {
    font-size: var(--text-display-lg);
  }
`;

export const GrowthChip = styled.span<{ $tone: GrowthTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => toneColor($tone)};

  & > svg {
    font-size: var(--text-lg);
  }

  @media ${QUERIES.tabletAndUp} {
    font-size: var(--text-lg);
  }
`;

export const GrowthValue = styled.span`
  white-space: nowrap;
`;

export const GrowthPercent = styled.span`
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  opacity: 0.8;
`;

// The trend is the point of the card, so it gets the most vertical room and
// the most space above it.
export const ChartArea = styled.div`
  width: 100%;
  min-width: 0;
  margin-top: ${SPACING.xl}px;
`;

// Reserves the compact chart height so swapping the real chart for this note
// (or back) never shifts the card.
export const ChartEmpty = styled.div`
  display: grid;
  place-items: center;
  min-height: ${HERO_CHART_HEIGHT.compact}px;
  text-align: center;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

// Quiet supporting line. Inline label + value pairs read as context for the
// headline rather than competing metrics. Sits below a divider, with space on
// both sides of the rule so it never crowds the chart or the rule itself.
export const SubStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.xl}px;
  margin-top: ${SPACING.xl}px;
  padding-top: ${SPACING.lg}px;
  border-top: 1px solid var(--color-border-light);
`;

export const SubStat = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

// Same eyebrow treatment as the portfolio label, only tracked a little tighter
// since it labels a value sitting right beside it rather than a section.
export const SubStatLabel = styled(PortfolioLabel)`
  letter-spacing: 0.12em;
`;

export const SubStatValue = styled.span`
  font-size: var(--text-md);
  font-weight: var(--font-weight-medium);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-default);
`;

// Savings-specific quiet footer: clarifies that the figures above are
// contributions only, before any interest is layered in.
export const Footnote = styled.p`
  margin: 0;
  margin-top: ${SPACING.md}px;
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
`;
